import type { SessionCookiePort } from '@elara/client-core/lib/session-cookie-port';

/**
 * Web `SessionCookiePort` implementation backed by `document.cookie` (ELARA-754).
 *
 * Mirrors the Firebase ID token into the `__session` cookie (55-minute max-age, `SameSite=Lax`,
 * `Secure` over https) so the embedded Mastra Studio — which authenticates via cookie, not a bearer
 * header — stays signed in. Clearing the token also clears the legacy `elara_studio_id_token` cookie.
 *
 * A missing `document` (SSR, hardened contexts) degrades to a no-op rather than throwing, honouring
 * the port contract. This file is web-only and must not be imported by `@elara/mobile`; React Native
 * composes a no-op `SessionCookiePort` instead.
 */

const SESSION_AUTH_COOKIE_NAME = '__session';
const SESSION_AUTH_COOKIE_MAX_AGE_SECONDS = 55 * 60;

export function createWebSessionCookiePort(): SessionCookiePort {
  return {
    setSessionToken(token: string | undefined): void {
      if (typeof document === 'undefined') return;

      const secureAttr =
        typeof window !== 'undefined' && window.location.protocol === 'https:' ? '; Secure' : '';
      if (!token) {
        document.cookie = `${SESSION_AUTH_COOKIE_NAME}=; Path=/; Max-Age=0; SameSite=Lax${secureAttr}`;
        document.cookie = `elara_studio_id_token=; Path=/; Max-Age=0; SameSite=Lax${secureAttr}`;
        return;
      }

      const encoded = encodeURIComponent(token);
      document.cookie =
        `${SESSION_AUTH_COOKIE_NAME}=${encoded}; Path=/; Max-Age=${SESSION_AUTH_COOKIE_MAX_AGE_SECONDS}; ` +
        `SameSite=Lax${secureAttr}`;
    },
  };
}
